import { API } from '../services/api';

export type SourceKey = 'cafe' | 'blog' | 'bobaedream' | 'theqoo' | 'blind' | 'cook82' | 'pann';
export type RadarKey = 'community' | 'life' | 'trend';
export type NewsStatusKey = 'new' | 'some' | 'many' | 'unknown';

export interface DeepAnalysis {
  issue: string;
  implication: string;
  angle: string;
  reporting: string;
  createdAt: string;
  trigger: 'auto' | 'manual';
}

export interface RadarRow {
  id: string;
  radar: RadarKey;
  title: string;
  summary: string;
  kind?: string;
  angle?: string;
  socialScore: number;
  sourceCount: number;
  venueCount: number;
  sourceKeys: SourceKey[];
  beats: string[];
  news: { status: NewsStatusKey; count: number };
  isNew: boolean;
  hasAnalysis: boolean;
  searchSurge: boolean;
  trend?: { thisWeek: number; lastWeek: number };
  popularity?: number;
  others?: number;
  single?: boolean;
}

export interface NewsArticle {
  title: string;
  link: string;
  pubDate: string;
}

export interface RadarSource {
  postId: string;
  source: SourceKey;
  link: string;
  title: string;
  venue: string;
  postedDate?: string;
  collectedAt: string;
  summary: string;
  popularity?: number;
}

export interface Feedback {
  up: number;
  down: number;
  used: number;
}

export interface RadarDetail extends Omit<RadarRow, 'news'> {
  news: { status: NewsStatusKey; count: number; articles: NewsArticle[]; checkedAt: string };
  sources: RadarSource[];
  analysis?: DeepAnalysis;
  feedback: Feedback;
  tracking?: { firstReportedAt?: string; leadDays?: number; checkedDays: number[] };
  keywords: string[];
}

export interface RoundRow {
  id: string;
  day?: string;
  startedAt: string;
  status: string;
  newCount: number;
  updatedCount: number;
}

export interface ItemsResponse {
  round: { id: string; day?: string; startedAt: string; status: string } | null;
  kinds: Record<string, number>;
  items: RadarRow[];
}

export interface HitStat {
  total: number;
  reported: number;
  hitRate: number;
  avgLeadDays: number | null;
}

export const fetchRounds = async (radar: RadarKey) =>
  (await API.get<{ rounds: RoundRow[] }>(`/radar/${radar}/rounds`)).data;

export const fetchItems = async (radar: RadarKey, roundId?: string, kind?: string) =>
  (await API.get<ItemsResponse>(`/radar/${radar}/items`, { params: { roundId, kind } })).data;

export const fetchItem = async (id: string) => (await API.get<RadarDetail>(`/radar/items/${id}`)).data;

export const requestAnalyze = async (id: string) =>
  (await API.post<{ analysis: DeepAnalysis }>(`/radar/items/${id}/analyze`)).data;

export const sendFeedback = async (id: string, value: 'up' | 'down' | 'used') =>
  (await API.post<{ feedback: Feedback }>(`/radar/items/${id}/feedback`, { value })).data;

export const fetchTrend = async (id: string) =>
  (await API.get<{ timeUnit?: 'date' | 'week'; points: { period: string; ratio: number }[] }>(`/radar/items/${id}/trend`)).data;

export const fetchStats = async () => (await API.get<{ stats: Record<RadarKey, HitStat> }>('/radar/stats')).data;

export const fetchBudget = async () =>
  (await API.get<{ monthSpentUsd: number; budgetUsd: number; stage2Blocked: boolean }>('/radar/budget')).data;

/** axios 오류에서 서버의 한국어 메시지를 꺼낸다 */
export function errorMessage(err: any, fallback = '요청에 실패했습니다'): string {
  return err?.response?.data?.message ?? fallback;
}
